import { DelayCategorySelector } from "@/shared/selectors/DelayCategorySelector";
import { DelayCodeTreePicker } from "@/shared/selectors/DelayCodeTreePicker";
import { CrewsFilter } from "@/shared/components/filters/CrewsFilter";
import {
	getProductSizes,
	getProductTypes,
	ProductSize,
	ProductType,
} from "@/shared/repositories/ProductRepository";
import SearchIcon from "@mui/icons-material/Search";
import { Autocomplete, Box, InputAdornment, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

type Props = {
	assetId: number | null;
	delayCategoryId: number | null;
	delayCodeId: number | null;
	crew: string | null;
	productTypeId: number | null;
	productSizeId: number | null;
	quickFilter: string;
	onDelayCategoryChange: (value: number | null) => void;
	onDelayCodeChange: (value: number | null) => void;
	onCrewChange: (value: string | null) => void;
	onProductTypeChange: (value: number | null) => void;
	onProductSizeChange: (value: number | null) => void;
	onQuickFilterChange: (value: string) => void;
};

export const DelaysManagerFilters = ({
	assetId,
	delayCategoryId,
	delayCodeId,
	crew,
	productTypeId,
	productSizeId,
	quickFilter,
	onDelayCategoryChange,
	onDelayCodeChange,
	onCrewChange,
	onProductTypeChange,
	onProductSizeChange,
	onQuickFilterChange,
}: Props) => {
	const { data: productTypes = [], isLoading: loadingTypes } = useQuery<
		ProductType[]
	>({
		queryKey: ["productTypes"],
		queryFn: getProductTypes,
	});

	const { data: productSizes = [], isLoading: loadingSizes } = useQuery<
		ProductSize[]
	>({
		queryKey: ["productSizes", productTypeId],
		queryFn: () => getProductSizes(productTypeId),
	});

	const productTypeOptions = [
		{ label: "All", value: null },
		...productTypes.map((pt) => ({ label: pt.Name, value: pt.Id })),
	];

	const productSizeOptions = [
		{ label: "All", value: null },
		...productSizes.map((ps) => ({ label: ps.Name, value: ps.Id })),
	];

	const selectedProductType =
		productTypeOptions.find((o) => o.value === productTypeId) ||
		productTypeOptions[0];

	const selectedProductSize =
		productSizeOptions.find((o) => o.value === productSizeId) ||
		productSizeOptions[0];

	return (
		<>
			<Box sx={{ flex: 2, minWidth: 180 }}>
				<DelayCodeTreePicker
					label="Reason"
					value={delayCodeId}
					onChange={(value) => onDelayCodeChange(value)}
					assetId={assetId}
				/>
			</Box>
			<Box sx={{ flex: 2, minWidth: 150 }}>
				<DelayCategorySelector
					label="Category"
					value={delayCategoryId}
					onChange={onDelayCategoryChange}
					assetId={assetId}
				/>
			</Box>
			<Box sx={{ flex: 1.5, minWidth: 120 }}>
				<Autocomplete
					value={selectedProductType}
					options={productTypeOptions}
					getOptionLabel={(o) => o.label}
					isOptionEqualToValue={(option, value) => option.value === value.value}
					onChange={(_, newValue) =>
						onProductTypeChange(newValue?.value ?? null)
					}
					disabled={loadingTypes}
					disableClearable
					size="small"
					renderInput={(params) => (
						<TextField {...params} label="Prod Type" variant="outlined" />
					)}
				/>
			</Box>
			<Box sx={{ flex: 2, minWidth: 150 }}>
				<Autocomplete
					value={selectedProductSize}
					options={productSizeOptions}
					getOptionLabel={(o) => o.label}
					isOptionEqualToValue={(option, value) => option.value === value.value}
					onChange={(_, newValue) =>
						onProductSizeChange(newValue?.value ?? null)
					}
					disabled={loadingSizes}
					disableClearable
					size="small"
					renderInput={(params) => (
						<TextField {...params} label="Product" variant="outlined" />
					)}
				/>
			</Box>
			<Box sx={{ flex: 1, minWidth: 80 }}>
				<CrewsFilter value={crew} onChange={onCrewChange} />
			</Box>
			<Box sx={{ flex: 1.5, minWidth: 120 }}>
				<TextField
					label="Search"
					variant="outlined"
					size="small"
					fullWidth
					value={quickFilter}
					onChange={(e) => onQuickFilterChange(e.target.value)}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" />
								</InputAdornment>
							),
						},
					}}
				/>
			</Box>
		</>
	);
};
